/**
 * Payroll (WALLIYO-0131), as the mockup draws it: a month, a summary per
 * currency, and a line per person.
 *
 * Every figure on this screen is rendered through one function that will not
 * take a bare number. Two currencies are two summary cards, never one added
 * together, because a sum of two currencies is not money and nobody could say
 * what it meant.
 */

import type { Money, PayrollLine, PayrollRun, PayrollTotals } from "@wallyo/contracts/console/hr";
import { t, type MessageId } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { hrHref } from "./hr-route";

export type PayrollState =
  | { status: "loading"; data: PayrollData | null }
  | { status: "ready"; data: PayrollData }
  | { status: "failed"; data: PayrollData | null; error: unknown };

export type PayrollData = {
  run: PayrollRun | null;
  lines: readonly PayrollLine[];
  totals: readonly PayrollTotals[];
  months: readonly string[];
  missingPay: readonly { staffMemberId: string; uuid: number; name: string }[];
};

const STATUS_LABEL: Record<PayrollLine["status"], MessageId> = {
  ready: "payroll.status.ready",
  held: "payroll.status.held",
  paid: "payroll.status.paid",
};

const STATUS_TONE: Record<PayrollLine["status"], string> = {
  ready: "pill",
  held: "pill warn",
  paid: "pill ok",
};

/**
 * An amount and its currency, always together.
 *
 * Grouped digits and the code beside them, in the reader's locale. No symbol
 * is guessed: a code is unambiguous everywhere, and a symbol is only
 * unambiguous where somebody already knows which country they are in.
 */
export function amount(money: Money): string {
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(money.amount)} ${money.currency}`;
}

function Totals({ totals }: { totals: readonly PayrollTotals[] }) {
  return (
    <div className="hr-totals">
      {totals.map((total) => (
        <div className="card" key={total.currency}>
          <h3>{total.currency}</h3>
          <dl className="hr-pairs">
            <dt>{t("payroll.total.people")}</dt>
            <dd>{total.people}</dd>
            <dt>{t("payroll.total.gross")}</dt>
            <dd>{amount({ amount: total.gross, currency: total.currency })}</dd>
            <dt>{t("payroll.total.contributions")}</dt>
            <dd>{amount({ amount: total.contributions, currency: total.currency })}</dd>
            <dt>{t("payroll.total.net")}</dt>
            <dd>
              <strong>{amount({ amount: total.net, currency: total.currency })}</strong>
            </dd>
          </dl>
        </div>
      ))}
    </div>
  );
}

export function PayrollView(props: {
  state: PayrollState;
  month: string;
  onMonth: (month: string) => void;
  onOpen: () => void;
  onHold: (line: PayrollLine, hold: boolean) => void;
  onClose: () => void;
  onRetry: () => void;
}) {
  const { state, month } = props;
  const data = state.data;
  const run = data?.run ?? null;
  const draft = run?.status === "draft";

  return (
    <>
      <div className="hr-search">
        <label className="field">
          <span className="label">{t("payroll.month")}</span>
          <select value={month} onChange={(event) => props.onMonth(event.currentTarget.value)}>
            {(data?.months ?? []).map((each) => (
              <option key={each} value={each}>
                {each}
              </option>
            ))}
            {data && !data.months.includes(month) && month !== "" ? <option value={month}>{month}</option> : null}
          </select>
        </label>
        <div className="grow" />
        <button className="btn" type="button" onClick={props.onOpen} disabled={state.status !== "ready"}>
          {t("payroll.open")}
        </button>
        {draft ? (
          <button className="btn" type="button" onClick={props.onClose}>
            {t("payroll.close")}
          </button>
        ) : null}
      </div>

      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("hr.failed")}</p>
          <ErrorNotice error={state.error} />
          <div className="btns">
            <button className="btn" type="button" onClick={props.onRetry}>
              {t("hr.retry")}
            </button>
          </div>
        </div>
      ) : null}

      {!data && state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("hr.loading")}</span>
        </div>
      ) : null}

      {data && run === null ? <div className="empty">{t("payroll.notOpen")}</div> : null}

      {data && run ? (
        <>
          {run.status === "closed" ? (
            <div className="banner" role="status">
              {t("payroll.closedOn", {
                when: formatDateTime(run.closedAt ?? ""),
                name: run.closedByName ?? "",
              })}
            </div>
          ) : null}

          <Totals totals={data.totals} />

          <section className="card">
            <h2>{t("payroll.lines.title")}</h2>
            <p className="foot">{t("payroll.lines.help")}</p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("payroll.col.person")}</th>
                    <th className="num">{t("payroll.col.gross")}</th>
                    <th className="num">{t("payroll.col.contributions")}</th>
                    <th className="num">{t("payroll.col.net")}</th>
                    <th>{t("payroll.col.status")}</th>
                    {draft ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((line) => (
                    <tr key={line.payrollLineId}>
                      <td>
                        <a href={hrHref({ uuid: line.uuid })}>{line.name}</a>
                      </td>
                      <td className="num mono">{amount(line.gross)}</td>
                      <td className="num mono">{amount(line.contributions)}</td>
                      <td className="num mono">
                        <strong>{amount(line.net)}</strong>
                      </td>
                      <td>
                        <span className={STATUS_TONE[line.status]}>{t(STATUS_LABEL[line.status])}</span>
                        {line.heldReason ? <div className="foot">{line.heldReason}</div> : null}
                      </td>
                      {draft ? (
                        <td className="num">
                          {line.status === "paid" ? null : (
                            <button
                              className="btn quiet small"
                              type="button"
                              onClick={() => props.onHold(line, line.status !== "held")}
                            >
                              {line.status === "held" ? t("payroll.release") : t("payroll.hold")}
                            </button>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}

      {/* Somebody a run cannot pay is named, not quietly left out: a run that
          skips somebody without saying so is one people trust and should not. */}
      {data && data.missingPay.length > 0 ? (
        <section className="card">
          <h2>{t("payroll.missing.title")}</h2>
          <p className="foot">{t("payroll.missing.help")}</p>
          <ul className="hr-plain">
            {data.missingPay.map((person) => (
              <li key={person.staffMemberId}>
                <a href={hrHref({ uuid: person.uuid })}>{person.name}</a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}
